import React from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  MessageSquare,
  ChevronRight,
  Sparkles,
  BrainCircuit,
  LineChart,
  Target,
  MessageCircle,
  ArrowUpRight,
  Zap,
  Link as LinkIcon,
  Users,
  Workflow,
  Database,
  GitBranch,
  Boxes,
} from "lucide-react";

const RevopsAgentLandingPage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background-default dark:bg-background-dark">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000,transparent)]" />
            <div className="absolute -inset-[10px] bg-gradient-to-tr from-primary/5 via-secondary/5 to-primary/5 dark:from-primary-dark/10 dark:via-secondary/10 dark:to-primary-dark/10 blur-3xl" />
          </div>

          <div className="relative max-w-6xl mx-auto">
            <div className="text-center space-y-8">
              {/* AI Badge */}
              <div className="inline-flex items-center justify-center p-2 rounded-2xl bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm border border-divider dark:border-divider-dark">
                <div className="flex items-center gap-3 px-4 py-2">
                  <Sparkles className="w-5 h-5 text-secondary" />
                  <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                    AI-Powered RevOps Assistant
                  </span>
                </div>
              </div>

              {/* Main Title */}
              <div className="space-y-1">
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-black tracking-tight">
                  <div className="text-text-primary dark:text-text-primaryDark">
                    Your Personal
                  </div>
                  <div className="text-primary dark:text-primary-dark">
                    RevOps Expert
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <span className="text-secondary text-4xl md:text-5xl lg:text-5xl">
                      Chat
                    </span>
                  </div>
                </h1>
              </div>

              {/* Description */}
              <p className="mt-6 text-xl text-text-secondary dark:text-text-secondaryDark max-w-3xl mx-auto leading-relaxed">
                Get instant answers to all your RevOps questions. Connect with
                Slack for seamless team collaboration and automated insights.
              </p>

              {/* CTA Section */}
              <div className="flex justify-center mt-8">
                <Link
                  to="/revops-agent/chat"
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-lg border-2 border-divider dark:border-divider-dark hover:border-secondary/50 dark:hover:border-secondary/50 transition-all duration-300 hover:translate-y-[-2px] text-text-primary dark:text-text-primaryDark"
                >
                  <span>Start Chatting</span>
                  <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>

              {/* Quick Actions */}
              <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto px-4">
                {[
                  {
                    icon: <BrainCircuit className="w-5 h-5" />,
                    text: "Revenue Operations Strategy",
                  },
                  {
                    icon: <LineChart className="w-5 h-5" />,
                    text: "Pipeline Analytics",
                  },
                  {
                    icon: <Target className="w-5 h-5" />,
                    text: "Process Optimization",
                  },
                ].map((action, index) => (
                  <button
                    key={index}
                    className="flex items-center gap-3 p-4 rounded-xl bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm border border-divider dark:border-divider-dark hover:border-secondary/50 dark:hover:border-secondary/50 transition-all duration-200"
                  >
                    <div className="text-secondary">{action.icon}</div>
                    <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                      {action.text}
                    </span>
                  </button>
                ))}
              </div>

              {/* Chat Preview */}
              <div className="mt-16 max-w-md mx-auto p-4 rounded-2xl bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark">
                <div className="flex items-start gap-3 mb-4">
                  <Bot className="w-8 h-8 text-primary dark:text-primary-dark" />
                  <div className="flex-1 p-3 rounded-lg bg-background-panel dark:bg-background-panelDark">
                    <p className="text-text-primary dark:text-text-primaryDark">
                      Hi! I'm your RevOps assistant. Ask me anything about
                      revenue operations, pipeline management, or process
                      optimization.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-panel dark:bg-background-panelDark">
                  <input
                    type="text"
                    placeholder="Type your question..."
                    className="flex-1 bg-transparent border-none focus:outline-none text-text-primary dark:text-text-primaryDark placeholder-text-secondary/50"
                    disabled
                  />
                  <Zap className="w-5 h-5 text-secondary" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid with Hover Effects */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background-paper dark:bg-background-paperDark relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_14px]" />

          <div className="relative max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-text-primary dark:text-text-primaryDark">
                Your HubSpot Expert Assistant
              </h2>
              <p className="mt-4 text-lg text-text-secondary dark:text-text-secondaryDark">
                Powered by AI and deep HubSpot knowledge
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <Database className="w-6 h-6" />,
                  title: "HubSpot Best Practices",
                  description:
                    "Get instant advice on CRM setup, workflow automation, and data management.",
                },
                {
                  icon: <Users className="w-6 h-6" />,
                  title: "Team Collaboration",
                  description:
                    "Seamlessly share insights and solutions through Slack integration.",
                },
                {
                  icon: <Workflow className="w-6 h-6" />,
                  title: "Workflow Optimization",
                  description:
                    "Optimize your sales and marketing processes with AI-powered suggestions.",
                },
                {
                  icon: <LinkIcon className="w-6 h-6" />,
                  title: "Community Integration",
                  description:
                    "Access curated links to HubSpot community resources and documentation.",
                },
                {
                  icon: <GitBranch className="w-6 h-6" />,
                  title: "Custom Solutions",
                  description:
                    "Get tailored answers for your specific HubSpot implementation.",
                },
                {
                  icon: <Boxes className="w-6 h-6" />,
                  title: "Multi-Portal Support",
                  description:
                    "Manage multiple HubSpot portals with consistent best practices.",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-2xl bg-background-panel/50 dark:bg-background-panelDark/50 border border-divider dark:border-divider-dark hover:border-secondary/50 dark:hover:border-secondary/50 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl hover:shadow-secondary/5"
                >
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <div className="text-secondary">{feature.icon}</div>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary dark:text-text-secondaryDark">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Demo Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-text-primary dark:text-text-primaryDark">
                  Experience AI-Powered
                  <br />
                  HubSpot Excellence
                </h2>
                <div className="space-y-4">
                  {[
                    "Get instant answers to complex HubSpot questions",
                    "Access best practices and community resources",
                    "Optimize your RevOps processes",
                    "Collaborate seamlessly with your team via Slack",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 text-text-secondary dark:text-text-secondaryDark"
                    >
                      <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-secondary" />
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Interactive Chat Preview */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-secondary/5 to-primary/5 dark:from-primary-dark/10 dark:via-secondary/10 dark:to-primary-dark/10 blur-3xl" />
                <div className="relative p-6 rounded-2xl bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Bot className="w-8 h-8 text-primary dark:text-primary-dark" />
                      <div className="flex-1 p-3 rounded-lg bg-background-panel dark:bg-background-panelDark">
                        <p className="text-text-primary dark:text-text-primaryDark">
                          How can I optimize my HubSpot workflows for better
                          lead nurturing?
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 pl-12">
                      <div className="flex-1 p-3 rounded-lg bg-secondary/10 dark:bg-secondary/20">
                        <p className="text-text-primary dark:text-text-primaryDark">
                          Here's a detailed guide on workflow optimization: 1.
                          Segment your leads based on behavior 2. Use
                          progressive profiling 3. Implement scoring mechanisms
                          Check out this HubSpot community resource: [link]
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Highlight */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background-paper dark:bg-background-paperDark relative overflow-hidden">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-text-primary dark:text-text-primaryDark mb-6">
              Seamless Integration with Your Workflow
            </h2>
            <p className="text-lg text-text-secondary dark:text-text-secondaryDark mb-12">
              Connect with Slack and access your HubSpot expert anywhere,
              anytime. Share insights with your team instantly.
            </p>

            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-background-panel dark:bg-background-panelDark border border-divider dark:border-divider-dark">
              <code className="text-sm text-text-secondary dark:text-text-secondaryDark">
                @hubspot-expert How do I set up custom properties in HubSpot?
              </code>
              <MessageCircle className="w-5 h-5 text-[#36C5F0]" />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RevopsAgentLandingPage;
